import React, { useState, useEffect, useMemo } from "react";
import Book from "../../../assets/Book.png";
import { FaGreaterThan } from "react-icons/fa";
import { GiTeacher } from "react-icons/gi";
import { BiUser } from "react-icons/bi";
import apiKey from "../../../config/apiKey";
import axios from "axios";
import group from "../../../assets/Group 300.png";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/** ---------- Small UI Helpers ---------- */
const timeZone = "Asia/Karachi";

function formatDateTime(dt) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dt));
  } catch {
    return new Date(dt).toLocaleString();
  }
}

function formatDate(dt) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(dt));
  } catch {
    return new Date(dt).toLocaleDateString();
  }
}

function timeFromNow(dt) {
  const now = Date.now();
  const t = new Date(dt).getTime();
  const diff = t - now;
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  const hours = Math.round(mins / 60);
  if (abs < 60000) return diff >= 0 ? "in <1 min" : "<1 min ago";
  if (mins < 60) return diff >= 0 ? `in ${mins} min` : `${mins} min ago`;
  if (hours < 24) return diff >= 0 ? `in ${hours} h` : `${hours} h ago`;
  const days = Math.round(hours / 24);
  return diff >= 0 ? `in ${days} d` : `${days} d ago`;
}

function InitialAvatar({ name }) {
  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#EE5F2B] to-[#2A3A54] text-white grid place-items-center font-semibold shadow-sm">
      {initials}
    </div>
  );
}

function StatCard({ label, value, to }) {
  return (
    <Link to={to} className="block group">
      <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <img src={group} alt="" className="w-12 opacity-60" />
        </div>
        <p className="mt-3 text-4xl font-bold tracking-tight text-gray-900">
          {value}
        </p>
        <div className="absolute bottom-0 right-0 translate-x-8 translate-y-8 w-28 h-28 rounded-full bg-[#EE5F2B]/10" />
      </div>
    </Link>
  );
}

function Badge({ children, tone = "info" }) {
  const tones = {
    info: "bg-blue-50 text-blue-700 ring-blue-200",
    success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    neutral: "bg-gray-50 text-gray-700 ring-gray-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ring-1 ${tones[tone]}`}>
      {children}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200" />
          <div>
            <div className="h-4 w-40 bg-gray-200 rounded" />
            <div className="h-3 w-56 bg-gray-100 rounded mt-2" />
          </div>
        </div>
      </td>
      <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
      <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded-full" /></td>
    </tr>
  );
}

/** ---------- Main Component ---------- */
const   Home = () => {
  const { t } = useTranslation();
  const [totalCount, setTotalCount] = useState(0);
  const [totalCountStd, setTotalCountStd] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [completedMeetings, setCompletedMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teachersData, setTeachersData] = useState([]);
  const [error, setError] = useState("");

  const admin = JSON.parse(localStorage.getItem("edo-admin"));
  const user = { name: admin ? admin.admin?.name : "" };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError("");
        setLoading(true);
        const headers = { Authorization: `Bearer ${admin?.token}` };

        const [teachersResponse, studentsResponse, meetingsResponse] =
          await Promise.all([
            axios.get(`${apiKey}/api/edolutions/admin/users/role/2`, { headers }),
            axios.get(`${apiKey}/api/edolutions/admin/users/role/3`, { headers }),
            axios.get(`${apiKey}/api/edolutions/admin/allmeetings`, { headers }),
          ]);

        const activeTeachers = (teachersResponse.data || []).filter(
          (item) => item.status === "Active"
        );
        setTotalCount(activeTeachers.length);
        setTeachersData(activeTeachers);

        const activeStudents = (studentsResponse.data || []).filter(
          (item) => item.status === "Active"
        );
        setTotalCountStd(activeStudents.length);

        const meetingsData = meetingsResponse.data?.meetings || [];
        setMeetings(meetingsData);

        const now = new Date();
        const upcoming = meetingsData
          .filter((m) => new Date(m.start_time) > now)
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

        const completed = meetingsData
          .filter((m) => new Date(m.start_time) <= now)
          .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));

        setUpcomingMeetings(upcoming);
        setCompletedMeetings(completed);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(t("adminDash.home.loadError"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [admin?.token]);

  const [detailsVisible, setDetailsVisible] = useState({});
  const toggleDetails = (id) =>
    setDetailsVisible((s) => ({ ...s, [id]: !s[id] }));

  const topTeachers = useMemo(() => teachersData.slice(0, 5), [teachersData]);

  return (
    <div className="my-4 w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <div className="flex items-center gap-3">
          {/* <img src={Book} alt="" className="w-8 h-8" /> */}
          <div>
            <p className="text-sm text-gray-500">{t("adminDash.home.welcomeBack")}</p>
            <p className="text-xl font-semibold">
              {t("adminDash.home.hey", { name: user.name })}
              <span className="ml-2 bg-clip-text text-transparent bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]">
                — {t("adminDash.home.dashboard")}
              </span>
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
          <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />
          {t("adminDash.home.status")}: <span className="font-medium text-gray-700">{t("adminDash.home.operational")}</span>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 z-10 grid place-items-center bg-white/70 backdrop-blur-sm rounded-2xl">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#EE5F2B] rounded-full animate-spin" />
            <p className="mt-3 text-sm font-semibold text-[#EE5F2B]">{t("adminDash.home.loading")}</p>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="mx-6 mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className={`transition-opacity ${loading ? "opacity-0" : "opacity-100"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard label={t("adminDash.home.totalMentors")} value={totalCount} to="/admins/mentor" />
              <StatCard label={t("adminDash.home.totalMentees")} value={totalCountStd} to="/admins/mentee" />
            </div>

            {/* Featured Teachers */}
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-5 py-4">
                <p className="text-lg font-semibold text-gray-900">
                  {t("adminDash.home.featuredMentors")}
                </p>
                <Link to="/admins/mentor" className="group inline-flex items-center gap-2 text-sm font-semibold text-[#EE5F2B] hover:underline">
                  {t("adminDash.home.viewAll")} <FaGreaterThan className="transition -translate-x-0 group-hover:translate-x-0.5" />
                </Link>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="px-6 py-3 text-xs font-semibold uppercase text-gray-500 tracking-wider w-5/12">
                      {t("adminDash.home.mentor")}
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold uppercase text-gray-500 tracking-wider w-2/12">
                        {t("adminDash.home.phone")}
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold uppercase text-gray-500 tracking-wider w-2/12">
                        {t("adminDash.home.joining")}
                      </th>
                      <th className="px-6 py-3 text-xs font-semibold uppercase text-gray-500 tracking-wider w-2/12">
                        {t("adminDash.home.type")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {!loading && topTeachers.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-6 py-6 text-center text-gray-500">
                          {t("adminDash.home.noMentors")}
                        </td>
                      </tr>
                    )}

                    {loading && (
                      <>
                        <SkeletonRow />
                        <SkeletonRow />
                        <SkeletonRow />
                      </>
                    )}

                    {!loading &&
                      topTeachers.map((teacher) => (
                        <tr key={teacher.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {/* Use avatar with fallback initials; keep your Book asset small to the side if you prefer */}
                              <InitialAvatar name={teacher.name} />
                              <div>
                                <p className="text-base font-medium text-gray-900">{teacher.name}</p>
                                <p className="text-xs text-gray-500">{teacher.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-700">{teacher.phone || "-"}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-gray-700">{formatDate(teacher.created_at)}</span>
                          </td>
                          <td className="px-6 py-4">
                            <Badge tone="success">
                              {teacher.type_id === 1 && t("adminDash.home.typeHome")}
                              {teacher.type_id === 2 && t("adminDash.home.typeOnline")}
                              {teacher.type_id === 3 && t("adminDash.home.typeBoth")}
                              {!teacher.type_id && "—"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right Column: Meetings */}
          <div className="space-y-6">
            {/* Upcoming */}
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-2">
                  <GiTeacher className="text-xl text-[#EE5F2B]" />
                  <p className="text-base font-semibold">{t("adminDash.home.upcomingMeetings")}</p>
                </div>
                <Link to="/admins/upcoming">
<button className="px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] rounded-lg shadow
  hover:from-[#2A3A54] hover:to-[#EE5F2B] focus:outline-none">
  {t("adminDash.home.allMeetings")}
</button>
                </Link>
              </div>

              <div className="max-h-72 overflow-auto px-5 pb-5 space-y-3">
                {loading && (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="animate-pulse rounded-xl border border-gray-100 p-4">
                        <div className="h-4 w-40 bg-gray-200 rounded" />
                        <div className="mt-2 h-3 w-24 bg-gray-100 rounded" />
                        <div className="mt-3 h-3 w-56 bg-gray-100 rounded" />
                      </div>
                    ))}
                  </>
                )}

                {!loading && upcomingMeetings.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-gray-500">
                    {t("adminDash.home.noUpcomingMeetings")}
                  </div>
                )}

                {!loading &&
                  upcomingMeetings.map((m) => (
                    <div key={m.id} className="rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow transition">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {t("adminDash.home.topic")}: <span className="capitalize">{m.topic}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t("adminDash.home.meetingId")}: <span className="font-medium text-gray-700">{m.id}</span>
                          </p>
                        </div>
                        <Badge tone="info">{timeFromNow(m.start_time)}</Badge>
                      </div>

                      {detailsVisible[m.id] && (
                        <div className="mt-3 space-y-1">
                          {m.agenda && <p className="text-sm text-gray-700">{m.agenda}</p>}
                          <p className="text-xs text-gray-500">
                            {t("adminDash.home.scheduledFor")}: <span className="font-medium text-gray-700">{formatDateTime(m.start_time)}</span>
                          </p>
                        </div>
                      )}

                      <button
                        onClick={() => toggleDetails(m.id)}
                        className="mt-3 text-sm font-medium text-[#EE5F2B] hover:underline"
                      >
                        {detailsVisible[m.id] ? t("adminDash.home.hideDetails") : t("adminDash.home.showDetails")}
                      </button>
                    </div>
                  ))}
              </div>
            </div>

            {/* Completed */}
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-2">
                  <BiUser className="text-xl text-[#EE5F2B]" />
                  <p className="text-base font-semibold">{t("adminDash.home.completedMeetings")}</p>
                </div>
                <Link to="/admins/completed">
                  <button className="px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]
  hover:from-[#2A3A54] hover:to-[#EE5F2B] focus:outline-none rounded-lg shadow">
                    {t("adminDash.home.allMeetings")}
                  </button>
                </Link>
              </div>

              <div className="max-h-72 overflow-auto px-5 pb-5 space-y-3">
                {loading && (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="animate-pulse rounded-xl border border-gray-100 p-4">
                        <div className="h-4 w-40 bg-gray-200 rounded" />
                        <div className="mt-2 h-3 w-24 bg-gray-100 rounded" />
                        <div className="mt-3 h-3 w-56 bg-gray-100 rounded" />
                      </div>
                    ))}
                  </>
                )}

                {!loading && completedMeetings.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-gray-500">
                    {t("adminDash.home.noCompletedMeetings")}
                  </div>
                )}

                {!loading &&
                  completedMeetings.map((m) => (
                    <div key={m.id} className="rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow transition">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">
                            {t("adminDash.home.topic")}: <span className="capitalize">{m.topic}</span>
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t("adminDash.home.meetingId")}: <span className="font-medium text-gray-700">{m.id}</span>
                          </p>
                        </div>
                        <Badge tone="neutral">{timeFromNow(m.start_time)}</Badge>
                      </div>

                      {detailsVisible[m.id] && (
                        <div className="mt-3 space-y-1">
                          {m.agenda && <p className="text-sm text-gray-700">{m.agenda}</p>}
                          <p className="text-xs text-gray-500">
                            {t("adminDash.home.completedOn")}: <span className="font-medium text-gray-700">{formatDateTime(m.start_time)}</span>
                          </p>
                        </div>
                      )}

                      <button
                        onClick={() => toggleDetails(m.id)}
                        className="mt-3 text-sm font-medium text-[#EE5F2B] hover:underline"
                      >
                        {detailsVisible[m.id] ? t("adminDash.home.hideDetails") : t("adminDash.home.showDetails")}
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Home;
